import Header from "../component/Header";
import { useEffect, useRef, useState } from "react";
import OverviewCard from "../component/OverviewCard";
import { MakerData } from "../types/type";
import BottomDrawer from "../component/BottomDrawer";
import { Box, Backdrop, CircularProgress} from "@mui/material";
import { useRecoilState, useRecoilValue } from "recoil"
import { CurrentMaker, RelevantMarkers, CurrentPosition, CurrentPoint, CurrentAmount, CurrentEmission} from "../store/currentState";
import { isIncludes, sortMarkersAtCenter } from "../hooks/useFunction";
import { useNavigate } from "react-router-dom";
import { getPlacesApi, getMyAreaApi, getMyPlacesApi, getPlacePointsApi, getPlaceStatisticsApi } from "../services/api/api";
import { mapDistance, myAreaDisplayPeriod} from "../constants/constants";
import AlertsSnackbar from "../component/AlertsSnackbar";
import { useCookies } from "react-cookie";
import { Avatar, Card, CardHeader, Typography } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import CardPagination from "../component/CardPagination";
import MyBottomNavigation from "../component/MyBottomNavigation";
import { useTranslation } from "react-i18next";


function MyArea(): JSX.Element {
  const { t } = useTranslation();
  const [cookies, setCookie, removeCookie] = useCookies(['recotech_mapapps_token']);
  const [isWaitingResponse, setIsWaitingResponse] = useState<boolean>(false);

  //BottomDrawerの設定
  const [openBottomDrawer, setOpenBottomDrawer] = useState<boolean>(false);
  const bottomDrawerRef = useRef<HTMLDivElement>(null);

  //SnackBarの設定
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [alertsMessage, setAlertsMessage] = useState<string>("");
  const [severity, setSeverity] = useState< "error" | "success" >("error");

  const [page, setPage] = useState(1)

  //markerを読み込みセットする
  const [marker, setMarker] = useRecoilState(CurrentMaker);
  const currentPosition = useRecoilValue(CurrentPosition);
  const [initialMarkers, setInitialMarkers] = useState<MakerData[]>([]);
  const [relevantMarkers, setRelevantMarkers] = useRecoilState(RelevantMarkers);
  const [area, setArea] = useState<{
    zipCode: string | null;
    address: string | null;
    latitude: number | null;
    longitude: number | null;
  }>({
    zipCode: null,
    address: null,
    latitude: null,
    longitude: null
  });
  const navigate = useNavigate();

  const [myPlaces, setMyPlaces] = useState<number[]>([]);
  const [point, setPoint] = useRecoilState(CurrentPoint);
  const [amount, setAmount] = useRecoilState(CurrentAmount);
  const [emission, setEmission] = useRecoilState(CurrentEmission);

  //api情報を読み込みセットする
  const fetchData = async () => {
    if (!cookies.recotech_mapapps_token) navigate("/");
    setMarker(null)
    setInitialMarkers([])
    const myArea = await getMyAreaApi(cookies.recotech_mapapps_token,removeCookie);
    setArea(myArea)
    const placesBounds = await getPlacesApi(100,myArea.latitude!,myArea.longitude!,mapDistance,setAlertsMessage,setSeverity,setOpenSnackbar,myArea.zipCode!)
    setInitialMarkers(placesBounds)
    //マーカー情報にMY回収場所情報を付与
    const myPlaces = await getMyPlacesApi(cookies.recotech_mapapps_token,removeCookie);
    setMyPlaces(myPlaces)
  };

  useEffect(() => {
    //現在地情報がなければMapへ飛ばす
    if(currentPosition.lat === 0 && currentPosition.lng === 0){
      navigate('/')
    }else{
      window.scrollTo(0, 0);
      fetchData();
    }
  }, []);

  //戻るをクリックした時の処理
  const handleBackClick = (() => {
    setOpenBottomDrawer(false)
  })

  //Cardをクリックした時の処理
  const handleCardClick = (async (targetMarker:MakerData) => {
    //選択した回収所の回収品目を一つでも持つ回収所を取得
    const searchMarkerData = initialMarkers.filter( function( marker ) {
    return isIncludes(targetMarker.categoryNames,marker.categoryNames) ;
    })

    //近い順に並び替えを行う
    sortMarkersAtCenter(searchMarkerData,currentPosition)

    //選択した回収所はリストから削除
    const withouttargetMarkerData = searchMarkerData.filter( function( marker ) {
    return marker.id !== targetMarker.id ;
    })

    setRelevantMarkers([])
    setMarker(targetMarker)

    const pointInfo = await getPlacePointsApi(targetMarker.id!,setAlertsMessage,setSeverity,setOpenSnackbar,setIsWaitingResponse);
    if(pointInfo) setPoint(pointInfo.amount)
    const statisticsInfo = await getPlaceStatisticsApi(targetMarker.id!,setAlertsMessage,setSeverity,setOpenSnackbar);
    if(statisticsInfo){
      setAmount(statisticsInfo.total_amount_sum)
      setEmission(statisticsInfo.reduction_carbon_emission_sum)
    }

    withouttargetMarkerData.map((marker) => {
    setRelevantMarkers((currentMarkers:MakerData[]) => [
        ...currentMarkers,{...marker}, //useRecoilStateで取得したobjctはreadonlyになっているため、スプレッド構文で新規に作り直す
    ]);
    });
    
    if (bottomDrawerRef.current) {
    bottomDrawerRef.current.scrollTop = 0;
    }
    setOpenBottomDrawer(true)
  })
    
  return (
    <>
      {isWaitingResponse ?
        <Backdrop open={isWaitingResponse} sx={{zIndex:10000}}>
        <CircularProgress color='inherit' />
      </Backdrop>
      :<></>
      }
      <AlertsSnackbar
        openSnackbar={openSnackbar}
        setOpenSnackbar={setOpenSnackbar}
        vertical={"top"}
        severity={severity}
        message={alertsMessage}
      />
      <Header />
      <BottomDrawer
        openBottomDrawer={openBottomDrawer}
        setOpenBottomDrawer={setOpenBottomDrawer}
        bottomDrawerRef={bottomDrawerRef}
        handleBackClick={handleBackClick}
        handleCardClick={handleCardClick}
      />
      <Box
        sx={{ p: 3 }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        {(area.address !== null && area.zipCode !== null)?
        <>
          <Card sx={{ maxWidth: 345 ,border: "none", boxShadow: "none" ,marginBottom:2}}>
            <CardHeader
              avatar={
                <Avatar>
                  <PersonIcon/>
                </Avatar>
              }
              title={t("pages.myArea.areaWithZip", { zipCode: area.zipCode })}
              subheader= {`${area.address}`}
            />
          </Card>
          {
            initialMarkers.filter((marker) => { //10日以内に登録されたマーカーのみ表示
              return (new Date(new Date().getTime() - marker.createdAt!.getTime()).getUTCDate() - 1 < myAreaDisplayPeriod)
            }).filter((marker,index)=>{ //ページネーションのためのフィルター
              return index >= (page-1)*10 && index < page*10
            })
            .map((marker, index) => (
              <Box marginBottom={1} marginTop={1}>
                  <OverviewCard
                    key={`OverviewCard${index}-bookmark`}
                    targetMarker={marker}
                    handleCardClick={handleCardClick}
                    myPlaces={myPlaces}
                  />
              </Box>
            ))
          }
          <CardPagination
            count={Math.ceil(initialMarkers.filter((marker) => {
              return (new Date(new Date().getTime() - marker.createdAt!.getTime()).getUTCDate() - 1 < myAreaDisplayPeriod)
            })?.length / 10)}
            page={page}
            setPage={setPage}              
          />
        </>
        :
        <>
          <Typography component="div" variant="caption">
            {t("pages.myArea.noArea")}
          </Typography>
          <Typography component="div" variant="caption">
            {t("pages.myArea.registerInstruction1")}
          </Typography>
          <Typography component="div" variant="caption">
            {t("pages.myArea.registerInstruction2")}
          </Typography>
        </>
        }
        </Box>
        <Box sx={{height: "100px"}}/>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          width="100%"
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <MyBottomNavigation />
        </Box>
    </>
  );
};
export default MyArea;
